import { AyumiaError, evaluateExpression } from '@ayumia/core/expressions';
import { isPrimitiveName } from './primitives.js';
import { resolveProperty } from './properties.js';
import { parseReference } from './scope.js';
import { DENIAL_CATEGORIES, parseService } from './services.js';
import { variableOrder } from './validate-manifest.js';
import { checkReturns, runFingerprint, runTimeout, runWriteTarget } from './run/sandbox.js';
/** Copie profonde d'une valeur de données (objets simples, listes, dates) : l'état ne partage rien. */
function clone(value) {
    if (Array.isArray(value))
        return value.map((entry) => clone(entry));
    if (value instanceof Date)
        return new Date(value.getTime());
    if (typeof value === 'object' && value !== null && Object.getPrototypeOf(value) === Object.prototype) {
        return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, clone(entry)]));
    }
    return value;
}
export function toServiceError(error) {
    if (error instanceof AyumiaError)
        return { category: error.category, code: error.code, message: error.message };
    return { category: 'EXECUTION_ERROR', code: 'COMPONENT_RUNTIME_ERROR', message: error instanceof Error ? error.message : String(error) };
}
/** Écrit `value` sous `segments` dans une copie de `target` (mise à jour immuable). */
function setPath(target, segments, value) {
    const [head, ...rest] = segments;
    const copy = { ...target };
    if (head === undefined)
        return copy;
    if (rest.length === 0)
        copy[head] = value;
    else {
        const current = copy[head];
        copy[head] = setPath(typeof current === 'object' && current !== null && !Array.isArray(current) ? current : {}, rest, value);
    }
    return copy;
}
export class ComponentInstance {
    manifest;
    options;
    state;
    inputs;
    status = 'ready';
    lastError;
    cached = null;
    bindings = new Map();
    listeners = new Set();
    children = new Map();
    order;
    template;
    disposed = false;
    constructor(manifest, options = {}) {
        this.manifest = manifest;
        this.options = options;
        if (manifest.template === undefined) {
            throw new Error(`${manifest.component.name} est fourni par le runtime (implementation) : son renderer le rend, il ne s'instancie pas comme un composite (ADR-099).`);
        }
        this.template = manifest.template;
        this.state = Object.fromEntries(Object.entries(manifest.state ?? {}).map(([name, declaration]) => [name, clone(declaration.initial ?? null)]));
        this.inputs = this.withDefaults(options.inputs ?? {});
        this.order = variableOrder(manifest.variables ?? {});
    }
    /** Abonnement du renderer (compatible `useSyncExternalStore`). */
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    setInputs(inputs) {
        this.inputs = this.withDefaults(inputs);
        this.invalidate();
    }
    dispose() {
        this.disposed = true;
        for (const child of this.children.values())
            child.dispose();
        this.children.clear();
        this.listeners.clear();
    }
    snapshot() {
        if (this.cached !== null)
            return this.cached;
        const variables = {};
        const base = { inputs: this.inputs, state: this.state, variables, record: this.options.record ?? null, context: this.options.context ?? {}, theme: this.options.theme ?? {} };
        let error = this.lastError;
        for (const name of this.order) {
            try {
                variables[name] = evaluateExpression(this.manifest.variables[name], base, this.evalOptions());
            }
            catch (caught) {
                variables[name] = null;
                error = toServiceError(caught);
            }
        }
        this.bindings = new Map();
        const tree = this.render(this.template, 'root', base, undefined, false)[0];
        const snapshot = { status: this.status, state: this.state, variables, tree };
        if (error !== undefined)
            snapshot.error = error;
        this.cached = snapshot;
        return snapshot;
    }
    /**
     * Instance d'un nœud composite, créée à la demande et gardée tant que le nœud existe : ses
     * entrées suivent les propriétés résolues du nœud, ses sorties déclenchent les événements du nœud.
     */
    childInstance(nodeId) {
        const node = this.snapshotNode(nodeId);
        if (node === undefined || node.kind !== 'component')
            return undefined;
        let child = this.children.get(nodeId);
        if (child === undefined) {
            const manifest = this.options.resolveComponent?.(node.component);
            if (manifest === undefined)
                return undefined;
            const childOptions = { ...this.options, inputs: node.props, onEmit: async (output, payload) => void (await this.dispatch(nodeId, output, payload)) };
            delete childOptions.record;
            child = new ComponentInstance(manifest, childOptions);
            this.children.set(nodeId, child);
        }
        else
            child.setInputs(node.props);
        return child;
    }
    /**
     * Exécute les actions d'un événement d'un nœud, dans l'ordre ; une action en échec arrête la
     * chaîne (après ses `on_error`). `payload` devient `$event`.
     */
    async dispatch(nodeId, event, payload = {}) {
        if (this.disposed)
            return { status: 'ignored' };
        if (this.cached === null)
            this.snapshot();
        const binding = this.bindings.get(nodeId);
        const handler = binding?.node.events?.[event];
        if (binding === undefined || handler === undefined)
            return { status: 'ignored' };
        this.status = 'busy';
        this.lastError = undefined;
        this.invalidate();
        const result = await this.runActions(handler.actions, binding, payload);
        if (!this.disposed) {
            if (result.status === 'failed') {
                this.status = DENIAL_CATEGORIES.has(result.error.category) ? 'permission_denied' : 'error';
                this.lastError = result.error;
            }
            else
                this.status = 'ready';
            this.invalidate();
        }
        return result;
    }
    // -------------------------------------------------------------------------------------------
    withDefaults(inputs) {
        const out = {};
        for (const [name, declaration] of Object.entries(this.manifest.inputs ?? {})) {
            out[name] = inputs[name] !== undefined ? inputs[name] : clone(declaration.default ?? null);
        }
        return out;
    }
    evalOptions() {
        return this.options.functions === undefined ? {} : { functions: this.options.functions };
    }
    invalidate() {
        this.cached = null;
        for (const listener of [...this.listeners])
            listener();
    }
    snapshotNode(id) {
        const find = (node) => {
            if (node.id === id)
                return node;
            for (const child of node.children) {
                const found = find(child);
                if (found !== undefined)
                    return found;
            }
            return undefined;
        };
        return find(this.snapshot().tree);
    }
    scopeFor(base, item, hasItem) {
        return hasItem ? { ...base, item } : base;
    }
    render(node, id, base, item, hasItem) {
        if (node.repeat !== undefined) {
            let items;
            try {
                items = evaluateExpression(node.repeat.items, this.scopeFor(base, item, hasItem), this.evalOptions());
            }
            catch (caught) {
                return [this.errorNode(node, id, toServiceError(caught))];
            }
            if (!Array.isArray(items))
                return [];
            const keyPath = node.repeat.key;
            return items.map((entry, index) => {
                let suffix = String(index);
                if (keyPath !== undefined) {
                    const key = evaluateExpression(`$item.${keyPath}`, { item: entry }, this.evalOptions());
                    if (typeof key === 'string' || typeof key === 'number')
                        suffix = String(key);
                }
                return this.renderOne(node, `${id}[${suffix}]`, base, entry, true);
            });
        }
        return [this.renderOne(node, id, base, item, hasItem)];
    }
    renderOne(node, id, base, item, hasItem) {
        const scope = this.scopeFor(base, item, hasItem);
        this.bindings.set(id, { node, item, hasItem });
        const rendered = {
            id,
            component: node.component,
            kind: isPrimitiveName(node.component) ? 'primitive' : 'component',
            props: {},
            style: {},
            events: Object.keys(node.events ?? {}),
            children: [],
        };
        try {
            for (const [name, value] of Object.entries(node.props ?? {}))
                rendered.props[name] = resolveProperty(value, scope, this.options.functions);
            for (const [name, value] of Object.entries(node.style ?? {}))
                rendered.style[name] = resolveProperty(value, scope, this.options.functions);
        }
        catch (caught) {
            rendered.error = toServiceError(caught);
        }
        (node.children ?? []).forEach((child, index) => {
            rendered.children.push(...this.render(child, `${id}/${child.key ?? index}`, base, item, hasItem));
        });
        return rendered;
    }
    errorNode(node, id, error) {
        return { id, component: node.component, kind: isPrimitiveName(node.component) ? 'primitive' : 'component', props: {}, style: {}, events: [], children: [], error };
    }
    actionScope(binding, event) {
        const snapshot = this.snapshot();
        const base = {
            inputs: this.inputs,
            state: this.state,
            variables: snapshot.variables,
            record: this.options.record ?? null,
            context: this.options.context ?? {},
            theme: this.options.theme ?? {},
            event,
        };
        return this.scopeFor(base, binding.item, binding.hasItem);
    }
    async runActions(actions, binding, event) {
        for (const action of actions) {
            if (this.disposed)
                return { status: 'ignored' };
            const result = await this.runAction(action, binding, event);
            if (result.status === 'failed')
                return result;
        }
        return { status: 'completed' };
    }
    async runAction(action, binding, event) {
        try {
            if (action.action === 'set') {
                const target = parseReference(action.target);
                if (target === null || target.root !== 'state' || target.segments[0] === undefined || !Object.hasOwn(this.manifest.state ?? {}, target.segments[0])) {
                    return { status: 'failed', error: { category: 'VALIDATION_ERROR', code: 'SET_TARGET_NOT_STATE', message: 'set ne modifie que l’état local déclaré.' } };
                }
                const value = evaluateExpression(action.value, this.actionScope(binding, event), this.evalOptions());
                this.state = setPath(this.state, target.segments, clone(value));
                this.invalidate();
                return { status: 'completed' };
            }
            if (action.action === 'run')
                return await this.runRun(action, binding, event);
            return await this.runApi(action, binding, event);
        }
        catch (caught) {
            return { status: 'failed', error: toServiceError(caught) };
        }
    }
    async runRun(action, binding, event) {
        const fingerprint = runFingerprint(action.code);
        const fail = async (error) => {
            const serviceError = { category: 'EXECUTION_ERROR', code: error.code, message: error.message };
            this.options.onRunError?.({ ...serviceError, fingerprint });
            const fallback = await this.runActions(action.on_error ?? [], binding, { error: { ...serviceError, fingerprint } });
            return fallback.status === 'ignored' ? fallback : { status: 'failed', error: serviceError };
        };
        if (this.options.runEnabled === false)
            return fail({ code: 'RUN_DISABLED', message: 'Le code run est désactivé pour ce tenant (core.component_run).' });
        const sandbox = this.options.runSandbox;
        if (sandbox === undefined)
            return fail({ code: 'RUN_UNAVAILABLE', message: 'Aucun bac à sable pour exécuter run.' });
        const stateNames = Object.keys(this.manifest.state ?? {});
        let request;
        try {
            const scope = this.actionScope(binding, event);
            const copy = (value) => JSON.parse(JSON.stringify(value ?? null));
            const locale = (this.options.context ?? {})['locale'];
            request = {
                code: action.code,
                context_version: action.context_version,
                timeout_ms: runTimeout(action),
                context: {
                    event: copy(event),
                    state: copy(this.state),
                    variables: copy(scope['variables']),
                    inputs: copy(this.inputs),
                    locale: typeof locale === 'string' ? locale : null,
                    now: (this.options.now?.() ?? new Date()).toISOString(),
                },
                scope: copy(scope),
                state_names: stateNames,
            };
        }
        catch (caught) {
            return fail({ code: 'RUN_EXCEPTION', message: `Contexte non sérialisable : ${caught instanceof Error ? caught.message : String(caught)}` });
        }
        let outcome;
        try {
            outcome = await sandbox.execute(request);
        }
        catch (caught) {
            outcome = { ok: false, error: { code: 'RUN_EXCEPTION', message: caught instanceof Error ? caught.message : String(caught) } };
        }
        if (this.disposed)
            return { status: 'ignored' };
        if (!outcome.ok)
            return fail(outcome.error);
        const problem = checkReturns(action.returns, outcome.value);
        if (problem !== null)
            return fail({ code: 'RUN_OUTPUT_INVALID', message: `Valeur rendue non conforme à returns : ${problem}.` });
        // L'hôte revérifie chaque cible : le bac à sable ne fait foi de rien.
        const names = new Set(stateNames);
        const writes = [];
        for (const write of outcome.writes) {
            const segments = typeof write.target === 'string' ? runWriteTarget(write.target, names) : null;
            if (segments === null)
                return fail({ code: 'RUN_FORBIDDEN_TARGET', message: `« ${String(write.target)} » n’est pas un état local déclaré.` });
            writes.push([segments, write.value]);
        }
        if (action.target !== undefined) {
            const segments = runWriteTarget(action.target, names);
            if (segments === null)
                return fail({ code: 'RUN_FORBIDDEN_TARGET', message: `« ${action.target} » n’est pas un état local déclaré.` });
            writes.push([segments, outcome.value]);
        }
        if (writes.length > 0) {
            let next = this.state;
            for (const [segments, value] of writes)
                next = setPath(next, segments, clone(value));
            this.state = next;
            this.invalidate();
        }
        return { status: 'completed' };
    }
    async runApi(action, binding, event) {
        const service = parseService(action.service);
        if (service === null || !(this.manifest.capabilities ?? []).includes(action.service)) {
            return { status: 'failed', error: { category: 'AUTHORIZATION_ERROR', code: 'SERVICE_NOT_DECLARED', message: `« ${action.service} » n'est pas une capacité déclarée du composant.` } };
        }
        const scope = this.actionScope(binding, event);
        const params = {};
        for (const [name, expression] of Object.entries(action.params ?? {}))
            params[name] = evaluateExpression(expression, scope, this.evalOptions());
        let outcome;
        if (service.kind === 'emit') {
            const { output, ...payload } = params;
            await this.options.onEmit?.(String(output), payload);
            outcome = { ok: true, value: null };
        }
        else if (service.kind === 'shell') {
            outcome = this.options.shell === undefined ? { ok: false, error: { category: 'CONFIGURATION_ERROR', code: 'SHELL_UNAVAILABLE', message: 'Aucune coquille pour ce composant.' } } : await this.options.shell.call(service.name, params);
        }
        else {
            outcome =
                this.options.gateway === undefined
                    ? { ok: false, error: { category: 'CONFIGURATION_ERROR', code: 'GATEWAY_UNAVAILABLE', message: 'Aucune passerelle de services.' } }
                    : await this.options.gateway.call(service.kind, service.name, params);
        }
        if (this.disposed)
            return { status: 'ignored' };
        if (outcome.ok) {
            if (action.result_to !== undefined) {
                const target = parseReference(action.result_to);
                if (target !== null && target.root === 'state' && target.segments[0] !== undefined && Object.hasOwn(this.manifest.state ?? {}, target.segments[0])) {
                    this.state = setPath(this.state, target.segments, clone(outcome.value));
                    this.invalidate();
                }
            }
            return this.runActions(action.on_success ?? [], binding, { value: outcome.value });
        }
        const fallback = await this.runActions(action.on_error ?? [], binding, { error: outcome.error });
        return fallback.status === 'ignored' ? fallback : { status: 'failed', error: outcome.error };
    }
}
//# sourceMappingURL=instance.js.map